import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(data?.message || 'Demande envoyée');
    } catch (error) {
      // On affiche le même message générique côté client pour éviter d'exposer les erreurs backend
      setSent(true);
      toast.success('Si cet email existe, un lien sécurisé vient d\u2019être envoyé.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à la connexion
        </Link>

        <Card className="shadow-xl animate-fade-in" data-testid="forgot-password-card">
          <CardHeader className="text-center">
            <div className="font-bold text-2xl font-['Manrope'] mb-2">
              <span className="text-primary">Capsule</span> Patrimoine
            </div>
            <CardTitle className="text-2xl font-['Manrope']">Mot de passe oublié</CardTitle>
            <CardDescription>
              {sent
                ? 'Vérifiez votre boîte mail dans quelques instants.'
                : 'Entrez votre email : nous vous enverrons un lien sécurisé (valable 48 h, usage unique) pour définir un nouveau mot de passe.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-6" data-testid="forgot-success">
                <div className="flex flex-col items-center gap-3 py-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="h-9 w-9 text-emerald-600" />
                  </div>
                  <p className="text-center text-sm text-slate-700">
                    Si l&apos;adresse <strong className="break-all">{email}</strong> existe dans notre base,
                    un email contenant un <strong>lien sécurisé pour définir votre mot de passe</strong> vient d&apos;être envoyé.
                  </p>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded text-xs text-amber-900">
                  <p className="font-semibold mb-1">Vous ne recevez rien ?</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Attendez 2-3 minutes (les emails peuvent prendre un peu)</li>
                    <li>Vérifiez votre dossier <strong>spam / courrier indésirable</strong></li>
                    <li>Vérifiez que l&apos;email saisi est bien celui utilisé lors de votre achat</li>
                  </ul>
                </div>

                <div className="flex flex-col gap-2">
                  <Link to="/login" data-testid="go-to-login-btn">
                    <Button className="w-full" size="lg">
                      Retour à la connexion
                    </Button>
                  </Link>
                  <button
                    type="button"
                    onClick={() => { setSent(false); setEmail(''); }}
                    className="text-xs text-muted-foreground hover:text-foreground text-center"
                    data-testid="retry-btn"
                  >
                    Essayer avec un autre email
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="vous@exemple.fr"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-9"
                      autoFocus
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={loading || !email}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Envoi en cours…' : 'Recevoir un lien sécurisé'}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Un email avec un lien sécurisé à usage unique vous sera envoyé. Aucun mot de passe ne transite par email.
                  Votre ancien mot de passe deviendra inactif.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
